/* eslint-disable
    new-cap
*/
import {css, StyleSheet} from 'aphrodite';
import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as SourceConstants from 'constants/SourceConstants';
import FileStates from 'FileStates';
import {Tag} from 'folder-items/FileConstants';
import {ConnectedMetadataEditor} from 'metadata/MetadataEditor';
import MetadataTabState from 'metadata/MetadataTabState';
import * as AaSequenceComplexPreviewTab from 'molbio/aa-sequences/complex/AaSequenceComplexPreviewTab';
import * as AaSequenceComplexPropertiesTab from 'molbio/aa-sequences/complex/AaSequenceComplexPropertiesTab';
import BiointelligentLinkComponentInfoBanner from 'molbio/biointelligent-links/BiointelligentLinkComponentInfoBanner';
import ReplaceBiointelligentLinksInfoBanner from 'molbio/biointelligent-links/replace-link/ReplaceBiointelligentLinksInfoBanner';
import {runDuplicatePolymerModal} from 'molbio/components/DuplicatePolymerModal';
import {trackMetadataTabViewIfValidSequence} from 'molbio/SequenceAnalyticsHelper';
import {getDedicatedGeneralNoteViewProps, getSharedNoteViewProps} from 'notebook/notes/tabs/NoteViewProps';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';

class MetadataTabView extends Backbone.View<MetadataTabState> {
  override initialize(): void {
    this.handleAnalyzeAaSequences = this.handleAnalyzeAaSequences.bind(this);
    this.updateName = this.updateName.bind(this);
    trackMetadataTabViewIfValidSequence(this.model.get('model'));
  }

  override render(): this {
    this.listenTo(this.model.get('model'), 'change', () => {
      this.renderMetadataEditor();
    });
    if (APIUtil.isNucleotideSequence(this.model.get('model').id)) {
      this.listenTo(this.model.get('model')._sequence(), 'change', () => {
        this.renderMetadataEditor();
      });
    }
    this.listenTo(this.model.get('model').fileState(), 'change', () => {
      this.renderMetadataEditor();
    });

    this.renderMetadataEditor();
    return this;
  }

  override remove(): this {
    ReactDOM.unmountComponentAtNode(this.el);
    super.remove();
    return this;
  }

  private handleAnalyzeAaSequences(proteinIds: Array<string>): void {
    const file = this.model.get('model');
    const fileState = FileStates.get().get(file.id);
    // TODO(T5091): Open the existing tag, if possible, once live updates are hooked up.
    fileState.buffers().addTab(new AaSequenceComplexPreviewTab.tabState({proteinIds}), {side: 'left'});
    fileState.buffers().addTab(new AaSequenceComplexPropertiesTab.tabState({proteinIds}), {side: 'right'});
  }

  async updateName(name: string): Promise<string> {
    const file = this.model.get('model');
    return await file.triggerUpdateNameAction(name);
  }

  private doesFileTypeSupportTeamAuthorLookup(): boolean {
    const fileId = this.model.get('model').id;
    return APIUtil.isEntry(fileId) || APIUtil.isProtocol(fileId);
  }

  renderMetadataEditor(): void {
    const file = this.model.get('model');
    const permissions = file.permissions();

    // If a file type supports registration, then setting a tag schema is gated on registry access.
    const shouldGateOnRegistryAccess = RegistryUtil.hasRegistrySupport(file.id);
    const userHasRegistryAccess = isFeatureEnabled({
      feature: RegistryFeature.REGISTRY_SHOW_DETAILS,
      item: {id: file.id},
    });
    const fileTypeSupportsTagSchema = TagSchemaUtil.hasFileSchemaSupport(file.id);
    const userHasTagSchemaAccessForFileType =
      (!shouldGateOnRegistryAccess || userHasRegistryAccess) && fileTypeSupportsTagSchema;
    const userHasRegistryAccessForFileType = shouldGateOnRegistryAccess && userHasRegistryAccess;
    const inRegistry =
      userHasRegistryAccessForFileType && file.folder().source.sourceType === SourceConstants.REGISTRY_TYPE;

    // TODO(T10473) Temporarily block on ELN_PRO until we reindex all files on main. We need to show
    // this for all ELN pro users, since we are removing review.authors.
    const hasAuthorSupport = licenses.hasEnterprisePlan() && file.authors() != null;
    const fileTags: Array<Tag> = _.sortBy(file.tags(), 'name');

    const modelId = this.model.get('model').id;
    renderToDOM(
      <ConnectedMetadataEditor
        archiveRecord={file.archiveRecord()}
        bufferOptionsText={!permissions.writable ? file.getBannerText() : undefined}
        createdAt={file.createdAt()}
        customInfoBanner={
          (APIUtil.isNucleotideSequence(file.id) ||
            (APIUtil.isProtein(file.id) && BenchlingConfig.MOLBIO_ENABLE_AA_PARTS)) &&
          BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS ? (
            <div className={css(styles.sequenceErrorBanners)}>
              <BiointelligentLinkComponentInfoBanner
                polymerId={file.id}
                onDuplicatePolymer={async () =>
                  await runDuplicatePolymerModal(
                    file,
                    fileTypeSupportsTagSchema ? (file.tagSchema()?.id ?? null) : null
                  )
                }
              />
              <ReplaceBiointelligentLinksInfoBanner
                isRegistered={inRegistry}
                polymerId={file.id}
                tagSchemaId={fileTypeSupportsTagSchema ? file.tagSchema()?.id : null}
              />
            </div>
          ) : null
        }
        fileId={file.id}
        fileName={file.name()}
        finalized={APIUtil.isBulkAssembly(modelId) ? file.finalized() : undefined}
        folder={file.folder()}
        projectFolderId={file.projectFolderId()}
        humanId={file.humanId()}
        updateName={async (name: string) => {
          await this.updateName(name);
        }}
        permissions={permissions}
        sequenceSubtype={file.subtype && file.subtype()}
        // For now, registry attributes and non-registry attributes are
        // gated on the same thing
        isRegistryWritable={permissions.writable}
        tags={fileTags}
        registryId={inRegistry ? file.folder().source.id : null}
        registryName={inRegistry ? file.folder().source.name : null}
        hasCustomTagSupport={TagSchemaUtil.hasCustomTagSupport(file.id)}
        hasFileSchemaSupport={userHasTagSchemaAccessForFileType}
        hasRegistrySupport={userHasRegistryAccessForFileType}
        tagSchema={fileTypeSupportsTagSchema ? file.tagSchema() : null}
        sequenceBases={APIUtil.isNucleotideSequence(modelId) ? file.bases() : null}
        sequenceCircular={APIUtil.isNucleotideSequence(modelId) ? file.circular() : null}
        sequenceModifiedNucleotides={
          APIUtil.isNucleotideSequence(modelId) ? file.modifiedNucleotides() : null
        }
        sequenceParts={APIUtil.isNucleotideSequence(modelId) ? file.parts().toReactJSON() : []}
        sequenceTranslations={APIUtil.isNucleotideSequence(modelId) ? file.translations().toReactJSON() : []}
        editURL={file.get('editURL') ?? null}
        schemaValidationState={file.schemaValidationState()}
        shouldAllowChangeCollection={
          inRegistry && !file.folder().source.disablePersonalCollections && permissions.appendable
        }
        shouldAllowTeamAuthorsLookup={this.doesFileTypeSupportTeamAuthorLookup()}
        onAnalyzeAaSequences={(proteinIds: Array<string>) => {
          this.handleAnalyzeAaSequences(proteinIds);
        }}
        authors={hasAuthorSupport ? file.authors() : null}
        instructionsEditorProps={{
          ...getSharedNoteViewProps(this.model),
          ...getDedicatedGeneralNoteViewProps(this.model, {fullScreen: false, minimalToolbar: true}),
        }}
        isNonInteractiveProcess={APIUtil.isProcess(file.id) ? file.isNonInteractiveProcess() : false}
      />,
      this.el
    );
  }
}

MetadataTabView.prototype.className = 'metadataTab buffer-tab buffer-tab--no-padding';

export default MetadataTabView;

const styles = StyleSheet.create({
  sequenceErrorBanners: {margin: '-12px 12px 12px 12px'},
});
